import ConfirmModal from "../../../confirmViewModal/ConfirmModal";
import { VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { VersionHistoryGroup } from "../types/versionHistory.types";
import { resolveVersionLabel } from "../utils/versionHistory.utils";

interface DeleteVersionDialogProps {
  version: VersionHistoryGroup | null;
  /** Full loaded list -- used only to work out which version the server will auto-activate when `version` is the active one. */
  versions: VersionHistoryGroup[];
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Deleting the active version promotes the next newest remaining version in its place
 * server-side (see useVersionDeletion) -- the highest versionNumber among the rest, regardless
 * of the array's own order, since that order isn't guaranteed by the API.
 */
const findNextActiveVersion = (
  deletedVersion: VersionHistoryGroup,
  versions: VersionHistoryGroup[]
): VersionHistoryGroup | null => {
  const candidates = versions.filter(
    candidate =>
      candidate.versionId && candidate.versionId !== deletedVersion.versionId && candidate.versionNumber != null
  );
  if (candidates.length === 0) return null;
  return candidates.reduce((newest, candidate) =>
    (candidate.versionNumber as number) > (newest.versionNumber as number) ? candidate : newest
  );
};

/**
 * Confirms permanently deleting a mapping version (DELETE /mapping-versions/:versionId) -- distinct
 * from DeleteVersionDetailsDialog, which only clears a version's year / units / price fields.
 */
export const DeleteVersionDialog = ({ version, versions, isDeleting, onConfirm, onCancel }: DeleteVersionDialogProps) => {
  const label = resolveVersionLabel(version);
  const nextActiveVersion = version?.isDefault ? findNextActiveVersion(version, versions) : null;

  const subTitle = version ? (
    <>
      <p>{VERSION_HISTORY_COPY.deleteVersionDialogBody}</p>
      {nextActiveVersion ? (
        <p className="tw-mt-2 tw-font-medium tw-text-foreground">
          {VERSION_HISTORY_COPY.deleteVersionActiveNotice(resolveVersionLabel(nextActiveVersion))}
        </p>
      ) : null}
    </>
  ) : (
    ""
  );

  return (
    <ConfirmModal
      viewModal={!!version}
      closeModal={onCancel}
      cancel={onCancel}
      approve={onConfirm}
      title={version ? VERSION_HISTORY_COPY.deleteVersionDialogTitle(label) : ""}
      subTitle={subTitle}
      confirmText={VERSION_HISTORY_COPY.deleteVersionConfirm}
      variant="destructive"
      isLoading={isDeleting}
      testId="delete-version-dialog"
    />
  );
};
